import FilterSelect from '@/components/base/FilterSelect';
import { auditImpacts } from '@/mocks/audit';
import type { AuditActorOption } from '@/utils/audit';

const categoryIcons: Record<string, string> = {
  All: 'ri-stack-line',
  Money: 'ri-money-dollar-circle-line',
  Access: 'ri-shield-user-line',
  Campaign: 'ri-megaphone-line',
  Config: 'ri-settings-3-line',
  Security: 'ri-shield-keyhole-line',
};

interface AuditToolbarProps {
  tabs: { id: string; label: string; count: number }[];
  category: string;
  onCategory: (id: string) => void;
  actors: AuditActorOption[];
  actorId: string;
  onActor: (value: string) => void;
  impact: string;
  onImpact: (value: string) => void;
  query: string;
  onQuery: (value: string) => void;
}

export default function AuditToolbar({
  tabs,
  category,
  onCategory,
  actors,
  actorId,
  onActor,
  impact,
  onImpact,
  query,
  onQuery,
}: AuditToolbarProps) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <FilterSelect
          value={category}
          onChange={onCategory}
          label="Category"
          className="w-full sm:w-56"
          options={tabs.map((tab) => ({ ...tab, icon: categoryIcons[tab.id] }))}
        />

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative">
            <span className="sr-only">Filter by actor</span>
            <i className="ri-user-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
            <select
              value={actorId}
              onChange={(event) => onActor(event.target.value)}
              className="h-9 w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 pl-8 pr-8 text-sm font-label text-foreground-700 focus:border-primary-400 focus:outline-none sm:w-48"
            >
              <option value="All actors">All actors</option>
              {actors.map((actor) => (
                <option key={actor.id} value={actor.id}>
                  {actor.name}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500" />
          </div>

          <div className="relative">
            <span className="sr-only">Filter by impact</span>
            <i className="ri-alert-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
            <select
              value={impact}
              onChange={(event) => onImpact(event.target.value)}
              className="h-9 w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 pl-8 pr-8 text-sm font-label text-foreground-700 focus:border-primary-400 focus:outline-none sm:w-44"
            >
              {auditImpacts.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500" />
          </div>

          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
            <i className="ri-search-line text-foreground-400" />
            <input
              type="text"
              value={query}
              onChange={(event) => onQuery(event.target.value)}
              placeholder="Search actor, action or reference..."
              className="h-9 w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none sm:w-64"
            />
          </div>
        </div>
      </div>
    </div>
  );
}